import React, { useEffect, useRef, useCallback, useState } from 'react';
import { WidgetSettings } from '../types';
import { UrlPreviewSettings } from './UrlPreviewSettings';

interface PreviewPanelProps {
  settings: WidgetSettings;
  currentDevice: 'desktop' | 'mobile';
}

export const PreviewPanel: React.FC<PreviewPanelProps> = ({
  settings,
  currentDevice
}) => {
  const desktopIframeRef = useRef<HTMLIFrameElement>(null);
  const mobileIframeRef = useRef<HTMLIFrameElement>(null);
  const desktopIframeLoadedRef = useRef(false);
  const mobileIframeLoadedRef = useRef(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [showUrlPreview, setShowUrlPreview] = useState<boolean>(false);
  
  // ウィジェットの開閉状態を親コンポーネントで管理（デバイス別）
  const [desktopWidgetOpen, setDesktopWidgetOpen] = useState(false);
  const [mobileWidgetOpen, setMobileWidgetOpen] = useState(false);
  
  // 現在のデバイスの状態を取得
  const getCurrentWidgetState = () => {
    return currentDevice === 'mobile' ? mobileWidgetOpen : desktopWidgetOpen;
  };
  
  // 現在のデバイスの状態を設定
  const setCurrentWidgetState = (isOpen: boolean) => {
    if (currentDevice === 'mobile') {
      setMobileWidgetOpen(isOpen);
    } else {
      setDesktopWidgetOpen(isOpen);
    }
  };
  
  // 現在アクティブなiframeRefを取得
  const getCurrentIframeRef = () => {
    return currentDevice === 'mobile' ? mobileIframeRef : desktopIframeRef;
  };
  
  // 現在アクティブなiframeLoadedRefを取得
  const getCurrentIframeLoadedRef = () => {
    return currentDevice === 'mobile' ? mobileIframeLoadedRef : desktopIframeLoadedRef;
  };

  // iframe初期化ヘルパー関数
  const initializeIframe = useCallback((iframeRef: React.RefObject<HTMLIFrameElement>, htmlContent: string) => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    
    try {
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);
      
      // iframe Blob URL設定中
      iframe.src = blobUrl;
      
      // クリーンアップ
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 1000);
    } catch (error) {
      // iframe初期化エラー
      // フォールバック: 直接HTML書き込み
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc) {
          doc.open();
          doc.write(htmlContent);
          doc.close();
        }
      } catch (fallbackError) {
        // フォールバック初期化エラー
      }
    }
  }, [showUrlPreview]);

  // iframe用のHTMLを生成（postMessageで通信）
  const generateIframeHTML = useCallback(() => {
    const currentOrigin = window.location.origin;
    
    // 設定にアイコンURLを追加（カスタムアイコンのみURLに変換、プリセットは数値のまま保持）
    const configWithUrls = {
      ...settings,
      bubbleIcon: settings.bubbleIcon === 'custom' ? settings.bubbleIconUrl : settings.bubbleIcon,
      userIcon: settings.userIcon === 'custom' ? settings.userIconUrl : settings.userIcon,
      botIcon: settings.botIcon === 'custom' ? settings.botIconUrl : settings.botIcon,
      sendIcon: settings.sendIcon === 'custom' ? settings.sendIconUrl : settings.sendIcon,
      closeIcon: settings.closeIcon === 'custom' ? settings.closeIconUrl : settings.closeIcon,
      iconBaseUrl: currentOrigin,
      widgetBaseUrl: currentOrigin
    };

    return `<!DOCTYPE html>
<html lang="ja">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>ChatBot Preview</title>
  <style>
    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }
    html, body {
      width: 100%;
      height: 100%;
      background-color: ${currentDevice === 'mobile' ? '#ffffff' : '#f3f4f6'};
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      overflow: hidden;
      position: relative;
      padding: 0;
      margin: 0;
      box-sizing: border-box;
    }
    
    /* iframe内でのバブル表示エリア確保 */
    body {
      min-width: 100px;
      min-height: 100px;
      /* モバイルプレビュー時は強制的に小さい幅に */
      ${currentDevice === 'mobile' ? 'max-width: 375px; max-height: 667px;' : ''}
    }
    
  </style>
</head>
<body>
  <script>
    let chatbotWidget = null;
    let isInitialized = false;
    
    // プレビュー環境フラグ
    window.IS_PREVIEW_MODE = true;
    
    // デバイス判定を強制的に設定
    window.FORCE_DEVICE_TYPE = '${currentDevice}';
    
    // プレビュー専用のストレージキー
    window.PREVIEW_STORAGE_KEY = 'chatbot-preview-state';
    
    // localStorageアクセスを安全に無効化（プレビュー環境用）
    window.localStorage = {
      getItem: () => null,
      setItem: () => {},
      removeItem: () => {},
      clear: () => {},
      key: () => null,
      length: 0
    };
    
    // sessionStorageはプレビュー状態管理のために活用
    // プレビュー専用のセーフなsessionStorage実装
    window.previewSessionStorage = {
      getItem: (key) => {
        try {
          return sessionStorage.getItem('preview_' + key);
        } catch (e) {
          return null;
        }
      },
      setItem: (key, value) => {
        try {
          sessionStorage.setItem('preview_' + key, value);
        } catch (e) {
          // 無視
        }
      },
      removeItem: (key) => {
        try {
          sessionStorage.removeItem('preview_' + key);
        } catch (e) {
          // 無視
        }
      }
    };
    
    // ChatbotWidgetクラス定義をここに直接埋め込み
    (function() {
      'use strict';

      // グローバル名前空間の汚染を防ぐ
      if (window.ChatbotWidget) {
        return;
      }

      // デフォルト設定
      const DEFAULT_CONFIG = {
        primaryColor: '#3182ce',
        secondaryColor: '#f5f5f5',
        offsetX: 20,
        offsetY: 20,
        desktopPosition: 'bottom-right',
        mobileOffsetX: 20,
        mobileOffsetY: 20,
        mobilePosition: 'bottom-right',
        width: 400,
        height: 600,
        bubbleSize: 60,
        mobileBubbleSize: 60,
        avatarIconSize: 64,
        uiIconSize: 24,
        headerFontSize: 16,
        messageFontSize: 14,
        inputFontSize: 14,
        headerHeight: 60,
        footerHeight: 60,
        bubbleShape: 'circle',
        bubbleIcon: '1',
        userIcon: 'user1',
        botIcon: 'bot1',
        sendIcon: 'send1',
        closeIcon: 'close1',
        headerText: 'チャットサポート',
        initialMessage: 'こんにちは！何かお手伝いできることはありますか？',
        useSafeArea: true,
        avoidSystemUI: true,
        apiEndpoint: '/.netlify/functions/chat',
        iconBaseUrl: null,
        widgetBaseUrl: null
      };

      // メッセージ定義
      const MESSAGES = {
        placeholder: 'メッセージを入力してください...',
        send: '送信',
        close: '閉じる',
        error_network: 'ネットワークエラーが発生しました',
        error_api: 'APIエラーが発生しました',
        typing: '入力中...'
      };

      // ChatbotWidgetクラス（簡略版）
      class ChatbotWidget {
        constructor(config) {
          this.config = { ...DEFAULT_CONFIG, ...config };
          this.messages = [];
          this.isCustomizing = false; // カスタマイズ中フラグを初期化
          
          // ローカルストレージから状態を復元
          this.isOpen = this.loadState();
          
          this.init();
        }

        init() {
          this.createStyles();
          this.createWidget();
          this.addEventListeners();
          
          // 初期状態を設定
          this.applyInitialState();
          
          if (this.config.initialMessage) {
            this.addMessage(this.config.initialMessage, 'assistant');
          }
        }

        // ローカルストレージから状態を読み込み
        loadState() {
          // postMessageを使って親ウィンドウから状態を要求
          if (window.parent && window.parent !== window) {
            // 親ウィンドウに状態読み込みを要求
            window.parent.postMessage({ type: 'LOAD_WIDGET_STATE' }, '*');
            // 非同期で状態が返ってくるため、初期は閉じた状態で開始
            return false;
          }
          return false; // デフォルトは閉じた状態
        }

        // プレビュー専用ストレージに状態を保存
        saveState() {
          try {
            const state = {
              isOpen: this.isOpen,
              timestamp: Date.now()
            };
            
            // postMessageを使って親ウィンドウに状態を保存要求
            if (window.parent && window.parent !== window) {
              // 親ウィンドウに状態保存を要求
              window.parent.postMessage({ 
                type: 'SAVE_WIDGET_STATE',
                state: state
              }, '*');
            }
          } catch (error) {
            // 状態保存エラー
          }
        }

        // 状態をリセット
        resetState() {
          try {
            // postMessageを使って親ウィンドウに状態リセットを要求
            if (window.parent && window.parent !== window) {
              window.parent.postMessage({ 
                type: 'SAVE_WIDGET_STATE',
                state: { isOpen: false, timestamp: Date.now() }
              }, '*');
            }
            // メッセージもクリア
            this.messages = [];
            if (this.messagesContainer) {
              this.messagesContainer.innerHTML = '';
            }
            // プレビュー状態をリセットしました
            // プレビューでは閉じた状態に戻すだけ
            this.isOpen = false;
            this.applyInitialState();
          } catch (error) {
            // 状態リセットエラー
          }
        }

        // 初期状態を適用
        applyInitialState() {
          if (this.isOpen) {
            this.bubble.classList.add('chatbot-hidden');
            this.window.classList.remove('chatbot-hidden');
          } else {
            this.bubble.classList.remove('chatbot-hidden');
            this.window.classList.add('chatbot-hidden');
          }
          // 初期状態適用
        }

        createStyles() {
          if (document.getElementById('chatbot-widget-styles')) {
            return;
          }

          const style = document.createElement('style');
          style.id = 'chatbot-widget-styles';
          style.textContent = \`
            .chatbot-widget {
              position: fixed;
              z-index: 99999999;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              font-size: 14px;
              line-height: 1.5;
            }
            
            .chatbot-bubble {
              cursor: pointer;
              transition: all 0.3s ease;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              border: none;
              outline: none;
            }
            
            .chatbot-bubble:hover {
              transform: scale(1.05);
            }
            
            .chatbot-window {
              background: white;
              border-radius: 12px;
              box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
              display: flex;
              flex-direction: column;
              overflow: hidden;
              position: fixed;
              z-index: 99999998;
              transform-origin: bottom right;
              transition: transform 0.3s ease-out, opacity 0.3s ease-out;
            }
            
            .chatbot-window.opening {
              animation: scaleInBottomRight 0.3s ease-out;
            }
            
            .chatbot-window.closing {
              animation: scaleOutBottomRight 0.3s ease-out forwards;
            }
            
            @keyframes scaleInBottomRight {
              from {
                opacity: 0;
                transform: scale(0);
                transform-origin: bottom right;
              }
              to {
                opacity: 1;
                transform: scale(1);
                transform-origin: bottom right;
              }
            }
            
            @keyframes scaleOutBottomRight {
              from {
                opacity: 1;
                transform: scale(1);
                transform-origin: bottom right;
              }
              to {
                opacity: 0;
                transform: scale(0);
                transform-origin: bottom right;
              }
            }
            
            .chatbot-header {
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 12px;
              border-bottom: 1px solid #e5e7eb;
              color: white;
              border-top-left-radius: 12px;
              border-top-right-radius: 12px;
              box-sizing: border-box;
            }
            
            .chatbot-header h3 {
              margin: 0;
              font-weight: 500;
              flex: 1;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            
            .chatbot-header-actions {
              display: flex;
              gap: 8px;
              margin-left: 8px;
            }
            
            .chatbot-header-btn {
              background: none;
              border: none;
              color: inherit;
              cursor: pointer;
              padding: 4px;
              border-radius: 4px;
              transition: background-color 0.2s;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            
            .chatbot-header-btn:hover {
              background-color: rgba(255, 255, 255, 0.2);
            }
            
            .chatbot-messages {
              flex: 1;
              overflow-y: auto;
              padding: 16px;
              background-color: \${this.config.secondaryColor};
            }
            
            .chatbot-hidden {
              display: none !important;
            }
            
            @media (max-width: 768px) {
              .chatbot-window:not(.chatbot-hidden) {
                position: fixed !important;
                top: 0 !important;
                left: 0 !important;
                right: 0 !important;
                bottom: 0 !important;
                width: 100vw !important;
                height: 100vh !important;
                height: 100dvh !important; /* 動的ビューポート高さ対応 */
                max-width: none !important;
                max-height: none !important;
                border-radius: 0 !important;
                z-index: 999999999 !important;
                overflow: hidden !important;
                display: flex !important;
                flex-direction: column !important;
              }
              
              /* モバイルでのキーボード表示対応 - フッター位置を適切に調整 */
              .chatbot-window.keyboard-visible {
                height: 100vh !important; /* 全画面維持 */
                height: 100dvh !important; /* 動的ビューポート高さ */
                top: 0 !important; /* 上端から表示 */
                bottom: 0 !important; /* 下端まで表示 */
                transform: none !important; /* 上にずらさない */
              }
              
              /* キーボード表示時の入力エリア調整 */
              .chatbot-window.keyboard-visible .chatbot-input-area {
                position: fixed !important;
                bottom: 0 !important;
                left: 0 !important;
                right: 0 !important;
                margin-bottom: 0 !important;
                background-color: white !important;
                border-top: 1px solid #e5e7eb !important;
                z-index: 10 !important;
              }
              
              .chatbot-messages {
                flex: 1 1 auto !important;
                overflow-y: auto !important;
                -webkit-overflow-scrolling: touch !important;
                padding: 16px !important;
                min-height: 200px !important; /* 最低限の高さを確保 */
              }
              
              .chatbot-input-area {
                flex: 0 0 auto !important;
                padding: 16px !important;
                padding-bottom: calc(16px + env(safe-area-inset-bottom, 10px)) !important;
                background-color: white !important;
                border-top: 1px solid #e5e7eb !important;
                position: absolute !important;
                bottom: 0 !important;
                left: 0 !important;
                right: 0 !important;
                z-index: 1 !important;
              }
              
              /* メッセージエリアに入力欄分の余白を確保 */
              .chatbot-messages {
                padding-bottom: 80px !important; /* 入力エリア分の余白 */
              }
              
              /* チャット入力欄のスタイル調整 */
              .chatbot-input {
                border: 2px solid #e5e7eb !important;
                border-radius: 12px !important;
                padding: 12px 16px !important;
                font-size: 16px !important; /* iOSでズーム防止 */
                line-height: 1.4 !important;
                background-color: #f9fafb !important;
              }
              
              .chatbot-input:focus {
                border-color: #3b82f6 !important;
                background-color: white !important;
                outline: none !important;
              }
            }
            
            /* 入力欄のスクロールバーを非表示 */
            .chatbot-input::-webkit-scrollbar {
              display: none;
            }
            
            .chatbot-input {
              -ms-overflow-style: none;
              scrollbar-width: none;
            }
          \`;
          
          document.head.appendChild(style);
        }

        createWidget() {
          // 最上位のコンテナを作成し、新しいスタッキングコンテキストを生成する
          this.rootContainer = document.createElement('div');
          this.rootContainer.className = 'chatbot-root';
          this.rootContainer.style.position = 'fixed';
          this.rootContainer.style.top = '0';
          this.rootContainer.style.left = '0';
          this.rootContainer.style.width = '100%';
          this.rootContainer.style.height = '100%';
          // z-indexが効くようにし、かつ他の要素に干渉しないようにポインタイベントを無効化
          this.rootContainer.style.zIndex = '2147483647'; // 32ビット整数の最大値（事実上の最上位）
          this.rootContainer.style.pointerEvents = 'none';

          // 既存のコンテナは、ポインタイベントを有効に戻す
          this.container = document.createElement('div');
          this.container.className = 'chatbot-widget';
          this.container.style.pointerEvents = 'auto'; // この中身だけクリックできるようにする
          this.setPosition();
          
          this.bubble = document.createElement('button');
          this.bubble.className = 'chatbot-bubble'; // デフォルトで表示
          this.bubble.innerHTML = this.getBubbleIcon();
          this.setBubbleStyles();
          
          this.window = document.createElement('div');
          this.window.className = 'chatbot-window chatbot-hidden'; // デフォルトで隠す
          this.setWindowStyles();
          
          this.createWindowContent();
          
          this.container.appendChild(this.bubble);
          this.container.appendChild(this.window);
          
          // rootContainerにウィジェットコンテナを追加
          this.rootContainer.appendChild(this.container);
          
          // 最終的にbodyに追加するのはrootContainer
          document.body.appendChild(this.rootContainer);
        }

        createWindowContent() {
          const header = document.createElement('div');
          header.className = 'chatbot-header';
          header.style.backgroundColor = this.config.primaryColor;
          header.style.fontSize = \`\${this.config.headerFontSize}px\`;
          header.style.height = \`\${this.config.headerHeight}px\`;
          header.style.minHeight = \`\${this.config.headerHeight}px\`;
          
          const title = document.createElement('h3');
          title.textContent = this.config.headerText;
          
          const actions = document.createElement('div');
          actions.className = 'chatbot-header-actions';
          
          const closeBtn = document.createElement('button');
          closeBtn.className = 'chatbot-header-btn';
          closeBtn.innerHTML = this.getCloseIcon();
          closeBtn.onclick = () => this.close();
          
          actions.appendChild(closeBtn);
          header.appendChild(title);
          header.appendChild(actions);
          
          this.messagesContainer = document.createElement('div');
          this.messagesContainer.className = 'chatbot-messages';
          this.messagesContainer.style.backgroundColor = this.config.secondaryColor;
          
          // 入力エリアを作成
          this.inputArea = document.createElement('div');
          this.inputArea.className = 'chatbot-input-area';
          this.inputArea.style.borderTop = '1px solid #e5e7eb';
          this.inputArea.style.backgroundColor = 'white';
          this.inputArea.style.height = \`\${this.config.footerHeight}px\`;
          this.inputArea.style.padding = '12px';  // 16px → 12px に変更（薄く）
          this.inputArea.style.display = 'flex';
          this.inputArea.style.gap = '12px';
          this.inputArea.style.alignItems = 'center';    // 中央配置でズレを防止
          this.inputArea.style.boxSizing = 'border-box';
          
          this.input = document.createElement('textarea');
          this.input.className = 'chatbot-input';
          this.input.placeholder = 'メッセージを入力してください...';
          this.input.style.flex = '1';
          this.input.style.border = '1px solid #d1d5db';
          this.input.style.borderRadius = '8px';
          this.input.style.padding = '8px 12px';
          this.input.style.outline = 'none';
          this.input.style.backgroundColor = 'white';
          this.input.style.resize = 'none';
          this.input.style.minHeight = '20px';
          this.input.style.maxHeight = '100px';
          this.input.style.fontFamily = 'inherit';
          this.input.style.lineHeight = '1.5';
          this.input.style.fontSize = \`\${this.config.inputFontSize}px\`;
          this.input.style.overflow = 'hidden';
          this.input.style.scrollbarWidth = 'none';
          this.input.style.msOverflowStyle = 'none';
          this.input.rows = 1;
          
          this.sendButton = document.createElement('button');
          this.sendButton.className = 'chatbot-send-btn';
          this.sendButton.style.border = 'none';
          this.sendButton.style.borderRadius = '50%';
          this.sendButton.style.padding = '0';  // パディングを0にして円形を保つ
          this.sendButton.style.color = 'white';
          this.sendButton.style.cursor = 'pointer';
          this.sendButton.style.transition = 'background-color 0.2s';
          this.sendButton.style.display = 'flex';
          this.sendButton.style.alignItems = 'center';
          this.sendButton.style.justifyContent = 'center';
          this.sendButton.style.width = '36px';   // 固定幅で円形確保
          this.sendButton.style.height = '36px';  // 固定高さで円形確保
          this.sendButton.style.minWidth = '36px';
          this.sendButton.style.minHeight = '36px';
          this.sendButton.style.flexShrink = '0';  // サイズ変更を防ぐ
          
          // プリセット送信アイコンのURLパターンをチェック
          const isPresetSendIconUrl = /\\/icons\\/send[1-4]\\.svg$/.test(this.config.sendIcon);
          const isPresetSendIcon = ['send1', 'send2', 'send3', 'send4'].includes(this.config.sendIcon) || isPresetSendIconUrl;
          const isCustomSendIcon = (this.config.sendIcon === 'custom' && this.config.sendIconUrl) || 
                                   (this.config.sendIcon.startsWith('http') && !isPresetSendIconUrl);
          
          if (isCustomSendIcon) {
            this.sendButton.style.backgroundColor = 'transparent';
          } else if (isPresetSendIcon) {
            this.sendButton.style.backgroundColor = this.config.primaryColor;
          }
          
          this.sendButton.innerHTML = this.getSendIcon();
          this.sendButton.onclick = () => this.sendMessage();
          
          this.inputArea.appendChild(this.input);
          this.inputArea.appendChild(this.sendButton);
          
          this.window.appendChild(header);
          this.window.appendChild(this.messagesContainer);
          this.window.appendChild(this.inputArea);
        }

        addEventListeners() {
          this.bubble.onclick = () => this.open();
          
          // 入力欄のイベントリスナー
          if (this.input) {
            this.input.addEventListener('keydown', (e) => {
              // IMEが変換の途中(isComposing)ではないことを確認する条件を追加
              if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
                e.preventDefault();
                this.sendMessage();
              }
            });
            
            this.input.addEventListener('input', () => {
              // イベントタイミング問題対策 - 次のイベントループで実行
              setTimeout(() => {
                this.autoResize();
                this.updateSendButton();
              }, 0);
            });
            
            // モバイルキーボード表示検出
            this.input.addEventListener('focus', () => {
              if (window.innerWidth <= 768) {
                setTimeout(() => {
                  this.window.classList.add('keyboard-visible');
                }, 300); // キーボード表示のアニメーション時間を考慮
              }
            });
            
            this.input.addEventListener('blur', () => {
              if (window.innerWidth <= 768) {
                this.window.classList.remove('keyboard-visible');
              }
            });
          }
          
          // モバイルでのビューポート変更検出（キーボード表示/非表示）
          let initialViewportHeight = window.innerHeight;
          window.addEventListener('resize', () => {
            if (window.innerWidth <= 768) {
              const currentHeight = window.innerHeight;
              const heightDiff = initialViewportHeight - currentHeight;
              
              // 高さが大幅に減少した場合はキーボードが表示されたと判断
              if (heightDiff > 150) {
                this.window.classList.add('keyboard-visible');
              } else {
                this.window.classList.remove('keyboard-visible');
              }
            }
          });
        }

        setPosition() {
          const isMobile = window.FORCE_DEVICE_TYPE ? window.FORCE_DEVICE_TYPE === 'mobile' : window.innerWidth <= 768;
          const position = isMobile ? this.config.mobilePosition : this.config.desktopPosition;
          
          // プレビュー環境用にオフセットを調整（見切れを防ぐ）
          const baseOffsetX = isMobile ? this.config.mobileOffsetX : this.config.offsetX;
          const baseOffsetY = isMobile ? this.config.mobileOffsetY : this.config.offsetY;
          
          // プレビュー環境では余裕を持たせたオフセット
          const currentBubbleSize = isMobile ? this.config.mobileBubbleSize : this.config.bubbleSize;
          const offsetX = Math.max(baseOffsetX, currentBubbleSize / 2 + 10);
          const offsetY = Math.max(baseOffsetY, currentBubbleSize / 2 + 10);
          
          this.container.style.position = 'fixed';
          this.container.style.top = 'auto';
          this.container.style.right = 'auto';
          this.container.style.bottom = 'auto';
          this.container.style.left = 'auto';
          this.container.style.zIndex = '9999';
          
          switch (position) {
            case 'top-left':
              this.container.style.left = \`\${offsetX}px\`;
              this.container.style.top = \`\${offsetY}px\`;
              break;
            case 'top-right':
              this.container.style.right = \`\${offsetX}px\`;
              this.container.style.top = \`\${offsetY}px\`;
              break;
            case 'bottom-left':
              this.container.style.left = \`\${offsetX}px\`;
              this.container.style.bottom = \`\${offsetY}px\`;
              break;
            case 'bottom-right':
            default:
              this.container.style.right = \`\${offsetX}px\`;
              this.container.style.bottom = \`\${offsetY}px\`;
              break;
          }
        }

        setBubbleStyles() {
          // バブルスタイル設定開始
          
          const isMobile = window.FORCE_DEVICE_TYPE ? window.FORCE_DEVICE_TYPE === 'mobile' : window.innerWidth <= 768;
          const size = isMobile ? 
            this.config.mobileBubbleSize : 
            this.config.bubbleSize;
          
          // サイズ設定は後でアイコンタイプに応じて行う
          
          // プリセットアイコンのURLパターンをチェック
          const isPresetIconUrl = /\\/icons\\/bubble[1-4]\\.svg$/.test(this.config.bubbleIcon);
          const isPresetIcon = ['bubble1', 'bubble2', 'bubble3', 'bubble4'].includes(this.config.bubbleIcon) || isPresetIconUrl || (this.config.bubbleIcon === 'custom' && (!this.config.bubbleIconUrl || this.config.bubbleIconUrl.trim() === ''));
          const isCustomIcon = (this.config.bubbleIcon === 'custom' && this.config.bubbleIconUrl && this.config.bubbleIconUrl.trim() !== '') || 
                                (this.config.bubbleIcon.startsWith('http') && !isPresetIconUrl);
          
          // アイコンタイプ判定完了
          
          if (isCustomIcon) {
            // カスタムアイコン: コンテナを透明にして、中のimg要素が直接ボタンとして機能
            // カスタムアイコン: 透明コンテナ設定
            this.bubble.style.width = 'auto';
            this.bubble.style.height = 'auto';
            this.bubble.style.padding = '0';
            this.bubble.style.border = 'none';
            this.bubble.style.borderRadius = '0';
            this.bubble.style.setProperty('background-color', 'transparent', 'important');
            this.bubble.style.setProperty('background-image', 'none', 'important');
            this.bubble.style.setProperty('box-shadow', 'none', 'important');
          } else if (isPresetIcon) {
            // プリセットアイコン: 正方形のバブル
            this.bubble.style.width = \`\${size}px\`;
            this.bubble.style.height = \`\${size}px\`;
            this.bubble.style.borderRadius = this.config.bubbleShape === 'circle' ? '50%' : '16px';
            
            // プリセットアイコンには背景色を設定、背景画像はクリア
            // プリセットアイコン: 背景色設定
            this.bubble.style.setProperty('background-image', 'none', 'important');
            this.bubble.style.setProperty('background-color', this.config.primaryColor, 'important');
            this.bubble.style.setProperty('box-shadow', '0 4px 12px rgba(0, 0, 0, 0.15)', 'important');
          }
          
          // バブルスタイル設定完了
        }

        setWindowStyles() {
          const isMobile = window.FORCE_DEVICE_TYPE ? window.FORCE_DEVICE_TYPE === 'mobile' : window.innerWidth <= 768;
          
          if (isMobile) {
            // モバイル時はCSSで制御するため、JavaScriptでの設定は最小限にする
            this.window.style.position = 'fixed';
            this.window.style.zIndex = '10000';
            // CSS @media クエリに任せる
          } else {
            this.window.style.width = \`\${this.config.width}px\`;
            this.window.style.height = \`\${this.config.height}px\`;
            this.window.style.position = 'fixed';
            this.window.style.borderRadius = '12px';
            this.window.style.bottom = '80px';
            this.window.style.right = '20px';
            this.window.style.top = 'auto';
            this.window.style.left = 'auto';
          }
        }

        open() {
          // ウィジェットを開く
          this.isOpen = true;
          this.bubble.classList.add('chatbot-hidden');
          this.window.classList.remove('chatbot-hidden');
          this.window.classList.add('opening');
          this.window.classList.remove('closing');
          this.saveState(); // 状態を保存
        }

        close() {
          // ウィジェットを閉じる
          this.isOpen = false;
          this.window.classList.add('closing');
          this.window.classList.remove('opening');
          
          // アニメーション完了後にウィンドウを隠す
          setTimeout(() => {
            this.window.classList.add('chatbot-hidden');
            this.bubble.classList.remove('chatbot-hidden');
            this.window.classList.remove('closing');
          }, 300); // アニメーション時間と同じ
          
          this.saveState(); // 状態を保存
        }

        addMessage(content, role) {
          const messageEl = document.createElement('div');
          messageEl.className = \`chatbot-message \${role}\`;
          messageEl.style.display = 'flex';
          messageEl.style.alignItems = 'flex-start';
          messageEl.style.gap = '8px';
          messageEl.style.marginBottom = '16px';
          
          if (role === 'user') {
            messageEl.style.flexDirection = 'row-reverse';
          }
          
          const avatarEl = document.createElement('div');
          avatarEl.style.flexShrink = '0';
          avatarEl.innerHTML = role === 'user' ? this.getUserIcon() : this.getBotIcon();
          
          const contentEl = document.createElement('div');
          contentEl.className = 'chatbot-message-content';
          contentEl.style.maxWidth = '80%';
          contentEl.style.padding = '12px 16px';
          contentEl.style.borderRadius = '18px';
          contentEl.style.wordWrap = 'break-word';
          contentEl.style.fontSize = \`\${this.config.messageFontSize}px\`;
          
          if (role === 'user') {
            contentEl.style.backgroundColor = this.config.primaryColor;
            contentEl.style.color = 'white';
            contentEl.style.marginLeft = '20%';
            contentEl.textContent = content;
          } else {
            contentEl.style.backgroundColor = '#ffffff';
            contentEl.style.color = '#111827';
            contentEl.style.marginRight = '20%';
            contentEl.textContent = content;
          }
          
          messageEl.appendChild(avatarEl);
          messageEl.appendChild(contentEl);
          this.messagesContainer.appendChild(messageEl);
          this.scrollToBottom();
          
          this.messages.push({ content, role, timestamp: Date.now() });
        }

        getBubbleIcon() {
          // バブルアイコン取得開始
          
          // カスタムアイコンかどうかを判定
          const isPresetIconUrl = /\\/icons\\/bubble[1-4]\\.svg$/.test(this.config.bubbleIcon);
          const isCustomIcon = (this.config.bubbleIcon === 'custom' && this.config.bubbleIconUrl && this.config.bubbleIconUrl.trim() !== '') || 
                                (this.config.bubbleIcon.startsWith('http') && !isPresetIconUrl);
          
          // アイコンタイプ判定完了
          
          const isMobile = window.FORCE_DEVICE_TYPE ? window.FORCE_DEVICE_TYPE === 'mobile' : window.innerWidth <= 768;
          const currentBubbleSize = isMobile ? this.config.mobileBubbleSize : this.config.bubbleSize;
          
          if (isCustomIcon) {
            // カスタムアイコンの場合はフルサイズの<img>で表示
            const customUrl = this.config.bubbleIcon === 'custom' ? this.config.bubbleIconUrl : this.config.bubbleIcon;
            // カスタムアイコンURL設定
            // カスタムアイコンは自由なサイズで表示、最大高さを制限
            return \`<img src="\${customUrl}" alt="カスタムアイコン" style="max-height: \${currentBubbleSize}px; height: auto; max-width: 200px; object-fit: contain;" />\`;
          } else {
            // プリセットアイコンの場合は従来通り<img>タグで表示
            const iconSize = currentBubbleSize * 0.5;
            const baseUrl = this.config.iconBaseUrl || window.location.origin;
            // カスタムアイコンが空の場合はデフォルトアイコンを使用
            const iconNumber = this.config.bubbleIcon === 'custom' ? 'bubble1' : this.config.bubbleIcon;
            const iconUrl = \`\${baseUrl}/icons/\${iconNumber}.svg\`;
            // プリセットアイコンURL生成完了
            return \`<img src="\${iconUrl}" alt="アイコン \${iconNumber}" style="width: \${iconSize}px; height: \${iconSize}px;" />\`;
          }
        }

        getCloseIcon() {
          const iconSize = this.config.uiIconSize;
          let iconUrl = '';
          let isCustomIcon = false;

          // 1. アイコンのURLを決定する
          if (this.config.closeIcon.startsWith('http')) {
            iconUrl = this.config.closeIcon;
          } else if (this.config.closeIcon === 'custom' && this.config.closeIconUrl) {
            iconUrl = this.config.closeIconUrl;
            isCustomIcon = true;
          } else {
            const baseUrl = this.config.iconBaseUrl || window.location.origin;
            iconUrl = \`\${baseUrl}/icons/\${this.config.closeIcon}.svg\`;
          }

          // 2. プリセットアイコンかどうかを判定する
          const isPresetIcon = ['close1', 'close2', 'close3', 'close4'].includes(this.config.closeIcon) ||
                               /\\/icons\\/close[1-4]\\.svg$/.test(iconUrl);

          // 3. プリセットアイコンであれば filter を適用し、カスタムアイコンなら適用しない
          if (isPresetIcon && !isCustomIcon) {
            return \`<img src="\${iconUrl}" alt="閉じる" style="width: \${iconSize}px; height: \${iconSize}px;" />\`;
          } else {
            return \`<img src="\${iconUrl}" alt="閉じる" style="width: \${iconSize}px; height: \${iconSize}px;" />\`;
          }
        }

        getUserIcon() {
          const iconSize = this.config.avatarIconSize;
          if (this.config.userIcon.startsWith('http')) {
            return \`<img src="\${this.config.userIcon}" alt="ユーザー" style="width: \${iconSize}px; height: \${iconSize}px;" />\`;
          } else if (this.config.userIcon === 'custom' && this.config.userIconUrl) {
            return \`<img src="\${this.config.userIconUrl}" alt="ユーザー" style="width: \${iconSize}px; height: \${iconSize}px;" />\`;
          } else {
            const baseUrl = this.config.iconBaseUrl || window.location.origin;
            const iconUrl = \`\${baseUrl}/icons/\${this.config.userIcon}.svg\`;
            return \`<img src="\${iconUrl}" alt="ユーザー" style="width: \${iconSize}px; height: \${iconSize}px;" />\`;
          }
        }

        getBotIcon() {
          const iconSize = this.config.avatarIconSize;
          if (this.config.botIcon.startsWith('http')) {
            return \`<img src="\${this.config.botIcon}" alt="ボット" style="width: \${iconSize}px; height: \${iconSize}px;" />\`;
          } else if (this.config.botIcon === 'custom' && this.config.botIconUrl) {
            return \`<img src="\${this.config.botIconUrl}" alt="ボット" style="width: \${iconSize}px; height: \${iconSize}px;" />\`;
          } else {
            const baseUrl = this.config.iconBaseUrl || window.location.origin;
            const iconUrl = \`\${baseUrl}/icons/\${this.config.botIcon}.svg\`;
            return \`<img src="\${iconUrl}" alt="ボット" style="width: \${iconSize}px; height: \${iconSize}px;" />\`;
          }
        }

        getSendIcon() {
          const iconSize = this.config.uiIconSize;
          let iconUrl = '';
          let isCustomIcon = false;

          // 1. アイコンのURLを決定する
          if (this.config.sendIcon.startsWith('http')) {
            iconUrl = this.config.sendIcon;
          } else if (this.config.sendIcon === 'custom' && this.config.sendIconUrl) {
            iconUrl = this.config.sendIconUrl;
            isCustomIcon = true;
          } else {
            const baseUrl = this.config.iconBaseUrl || window.location.origin;
            iconUrl = \`\${baseUrl}/icons/\${this.config.sendIcon}.svg\`;
          }

          // 2. プリセットアイコンかどうかを判定する
          const isPresetIcon = ['send1', 'send2', 'send3', 'send4'].includes(this.config.sendIcon) ||
                               /\\/icons\\/send[1-4]\\.svg$/.test(iconUrl);

          // 3. プリセットアイコンであれば filter を適用し、カスタムアイコンなら適用しない
          if (isPresetIcon && !isCustomIcon) {
            return \`<img src="\${iconUrl}" alt="送信" style="width: \${iconSize}px; height: \${iconSize}px;" />\`;
          } else {
            return \`<img src="\${iconUrl}" alt="送信" style="width: \${iconSize}px; height: \${iconSize}px;" />\`;
          }
        }

        sendMessage() {
          const content = this.input.value.trim();
          if (!content) return;
          
          this.addMessage(content, 'user');
          this.input.value = '';
          this.updateSendButton();
          this.autoResize();
          
          // プレビューでは自動応答メッセージを表示
          setTimeout(() => {
            this.addMessage('これはプレビューです。実際の埋め込み環境では API による応答が行われます。', 'assistant');
          }, 1000);
        }

        updateSendButton() {
          if (this.sendButton && this.input) {
            this.sendButton.disabled = !this.input.value.trim();
            this.sendButton.style.opacity = this.input.value.trim() ? '1' : '0.5';
          }
        }

        autoResize() {
          if (this.input && this.inputArea) {
            this.input.style.height = 'auto';
            let inputHeight = Math.min(this.input.scrollHeight, 100);
            
            // 空の場合は明示的に適切な初期高さに設定
            if (this.input.value.trim() === '') {
              // fontSize * lineHeight + padding = 計算式で適切な高さを算出
              const fontSize = this.config.inputFontSize || 14;
              const lineHeight = 1.5;
              const paddingVertical = 16; // 8px * 2
              inputHeight = Math.max(20, Math.ceil(fontSize * lineHeight + paddingVertical));
            }
            
            this.input.style.height = inputHeight + 'px';
            
            // フッターの高さも入力欄の高さに合わせて動的に調整
            // inputAreaのpadding(24px) のみ = 実際に必要な余白
            const totalPadding = 24;
            
            // 空の場合または入力欄が初期サイズの場合は初期値に戻す
            let footerHeight;
            if (this.input.value.trim() === '') {
              footerHeight = this.config.footerHeight;
            } else if (inputHeight <= 40) {
              // 一行入力の場合は拡張しない
              footerHeight = this.config.footerHeight;
            } else {
              // 複数行の場合のみ拡張
              footerHeight = Math.max(this.config.footerHeight, inputHeight + totalPadding);
            }
            
            this.inputArea.style.height = footerHeight + 'px';
            
            // autoResize完了
          } else {
            // autoResize: 要素参照エラー
          }
        }

        scrollToBottom() {
          if (this.messagesContainer) {
            setTimeout(() => {
              this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
            }, 100);
          }
        }

        updateConfig(newConfig) {
          // シンプルで確実なアプローチ
          const wasOpen = this.isOpen;
          // updateConfig開始
          
          // 設定を更新
          this.config = { ...this.config, ...newConfig };
          
          // スタイル更新（状態に影響しないもの）
          this.setPosition();
          this.setBubbleStyles();
          this.setWindowStyles();
          
          // アイコンを更新
          this.bubble.innerHTML = this.getBubbleIcon();
          
          // ヘッダーカラーを更新
          const header = this.window.querySelector('.chatbot-header');
          if (header) {
            header.style.backgroundColor = this.config.primaryColor;
            header.style.fontSize = \`\${this.config.headerFontSize}px\`;
            header.style.height = \`\${this.config.headerHeight}px\`;
            header.style.minHeight = \`\${this.config.headerHeight}px\`;
            
            // ヘッダータイトルも更新
            const title = header.querySelector('h3');
            if (title) {
              title.textContent = this.config.headerText;
            }
          }
          
          // フッター（入力エリア）の高さを更新
          if (this.inputArea) {
            this.inputArea.style.height = \`\${this.config.footerHeight}px\`;
          }
          
          // メッセージエリアの背景色を更新
          if (this.messagesContainer) {
            this.messagesContainer.style.backgroundColor = this.config.secondaryColor;
            
            // 既存のメッセージのフォントサイズを更新
            const messages = this.messagesContainer.querySelectorAll('.chatbot-message-content');
            messages.forEach(msg => {
              msg.style.fontSize = \`\${this.config.messageFontSize}px\`;
            });
            
            // アバターアイコンのサイズとスタイルを更新
            const userAvatars = this.messagesContainer.querySelectorAll('[alt="ユーザー"]');
            userAvatars.forEach(avatar => {
              avatar.style.width = \`\${this.config.avatarIconSize}px\`;
              avatar.style.height = \`\${this.config.avatarIconSize}px\`;
              avatar.style.borderRadius = '';
              avatar.style.backgroundColor = '';
              avatar.style.padding = '';
              // アイコンのURLも更新
              if (this.config.userIcon.startsWith('http')) {
                avatar.src = this.config.userIcon;
              } else if (this.config.userIcon === 'custom' && this.config.userIconUrl) {
                avatar.src = this.config.userIconUrl;
              } else {
                const baseUrl = this.config.iconBaseUrl || window.location.origin;
                avatar.src = \`\${baseUrl}/icons/\${this.config.userIcon}.svg\`;
              }
            });
            
            const botAvatars = this.messagesContainer.querySelectorAll('[alt="ボット"]');
            botAvatars.forEach(avatar => {
              avatar.style.width = \`\${this.config.avatarIconSize}px\`;
              avatar.style.height = \`\${this.config.avatarIconSize}px\`;
              avatar.style.borderRadius = '';
              avatar.style.backgroundColor = '';
              avatar.style.padding = '';
              // アイコンのURLも更新
              if (this.config.botIcon.startsWith('http')) {
                avatar.src = this.config.botIcon;
              } else if (this.config.botIcon === 'custom' && this.config.botIconUrl) {
                avatar.src = this.config.botIconUrl;
              } else {
                const baseUrl = this.config.iconBaseUrl || window.location.origin;
                avatar.src = \`\${baseUrl}/icons/\${this.config.botIcon}.svg\`;
              }
            });
          }
          
          // 送信ボタンの色を更新
          if (this.sendButton) {
            // プリセット送信アイコンのURLパターンをチェック
            const isPresetSendIconUrl = /\\/icons\\/send[1-4]\\.svg$/.test(this.config.sendIcon);
            const isPresetSendIcon = ['send1', 'send2', 'send3', 'send4'].includes(this.config.sendIcon) || isPresetSendIconUrl;
            const isCustomSendIcon = (this.config.sendIcon === 'custom' && this.config.sendIconUrl) || 
                                     (this.config.sendIcon.startsWith('http') && !isPresetSendIconUrl);
            
            if (isCustomSendIcon) {
              this.sendButton.style.backgroundColor = 'transparent';
              this.sendButton.style.border = 'none';
            } else if (isPresetSendIcon) {
              this.sendButton.style.backgroundColor = this.config.primaryColor;
              this.sendButton.style.border = 'none';
            }
            this.sendButton.innerHTML = this.getSendIcon();
          }
          
          // 入力欄のフォントサイズを更新
          if (this.input) {
            this.input.style.fontSize = \`\${this.config.inputFontSize}px\`;
          }
          
          // 閉じるボタンのアイコンを更新
          const closeBtn = this.window.querySelector('.chatbot-header-btn');
          if (closeBtn) {
            closeBtn.innerHTML = this.getCloseIcon();
          }
          
          // 状態を保持したまま更新完了
          this.isOpen = wasOpen;
          
          // 状態に基づいてクラスを確実に設定
          if (wasOpen) {
            this.bubble.classList.add('chatbot-hidden');
            this.window.classList.remove('chatbot-hidden');
            // 開いた状態を維持
          } else {
            this.bubble.classList.remove('chatbot-hidden');
            this.window.classList.add('chatbot-hidden');
            // 閉じた状態を維持
          }
          
          // updateConfig完了
        }

        destroy() {
          if (this.container) {
            this.container.remove();
          }
          const styles = document.getElementById('chatbot-widget-styles');
          if (styles) {
            styles.remove();
          }
        }
      }

      // グローバルAPI
      window.ChatbotWidget = {
        init: function(config) {
          if (window.chatbotWidgetInstance) {
            window.chatbotWidgetInstance.destroy();
          }
          window.chatbotWidgetInstance = new ChatbotWidget(config);
          return window.chatbotWidgetInstance;
        },
        
        destroy: function() {
          if (window.chatbotWidgetInstance) {
            window.chatbotWidgetInstance.destroy();
            window.chatbotWidgetInstance = null;
          }
        },
        
        updateConfig: function(newConfig) {
          if (window.chatbotWidgetInstance) {
            window.chatbotWidgetInstance.updateConfig(newConfig);
          }
        },
        
        getInstance: function() {
          return window.chatbotWidgetInstance;
        }
      };
    })();
    
    // 初期設定で初期化
    const initialConfig = ${JSON.stringify(configWithUrls, null, 6)};
    
    function initializeWidget() {
      if (!isInitialized) {
        chatbotWidget = window.ChatbotWidget.init(initialConfig);
        isInitialized = true;
        // iframe内Widget初期化完了
        
        // 初期化完了を親に通知
        window.parent.postMessage({
          type: 'WIDGET_INITIALIZED'
        }, '*');
      }
    }
    
    // 親からのメッセージを受信
    window.addEventListener('message', function(event) {
      if (event.data.type === 'UPDATE_CONFIG') {
        // iframe内設定更新
        if (window.ChatbotWidget && isInitialized) {
          window.ChatbotWidget.updateConfig(event.data.config);
        }
      } else if (event.data.type === 'resetState') {
        // iframe内状態リセット
        if (window.ChatbotWidget && isInitialized) {
          const instance = window.ChatbotWidget.getInstance();
          if (instance && instance.resetState) {
            instance.resetState();
          }
        }
      } else if (event.data.type === 'WIDGET_STATE_RESPONSE') {
        // 親ウィンドウから状態応答
        if (window.ChatbotWidget && isInitialized && event.data.state) {
          const instance = window.ChatbotWidget.getInstance();
          if (instance && event.data.state.isOpen) {
            // 保存された開いた状態を復元
            instance.isOpen = true;
            instance.applyInitialState();
          }
        }
      }
    });
    
    // DOM読み込み完了後に初期化
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeWidget);
    } else {
      initializeWidget();
    }
  </script>
</body>
</html>`;
  }, [settings, currentDevice]);

  // 設定をiframeに送信
  const sendConfigToIframe = useCallback((config: WidgetSettings) => {
    if (getCurrentIframeRef().current && getCurrentIframeLoadedRef().current) {
      const currentOrigin = window.location.origin;
      
      const configWithUrls = {
        ...config,
        bubbleIcon: config.bubbleIcon === 'custom' ? config.bubbleIconUrl : `${currentOrigin}/icons/bubble${config.bubbleIcon}.svg`,
        userIcon: config.userIcon === 'custom' ? config.userIconUrl : `${currentOrigin}/icons/${config.userIcon}.svg`,
        botIcon: config.botIcon === 'custom' ? config.botIconUrl : `${currentOrigin}/icons/${config.botIcon}.svg`,
        sendIcon: config.sendIcon === 'custom' ? config.sendIconUrl : `${currentOrigin}/icons/${config.sendIcon}.svg`,
        closeIcon: config.closeIcon === 'custom' ? config.closeIconUrl : `${currentOrigin}/icons/${config.closeIcon}.svg`,
        iconBaseUrl: currentOrigin,
        widgetBaseUrl: currentOrigin
      };

      // 設定をiframeに送信
      
      getCurrentIframeRef().current?.contentWindow?.postMessage({
        type: 'UPDATE_CONFIG',
        config: configWithUrls
      }, '*');
    }
  }, []);

  // iframeからのメッセージを受信
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // オリジンチェック（セキュリティ対策）
      if (event.origin !== window.location.origin) {
        return;
      }
      
      // どちらのiframeからのメッセージかを判定
      const desktopIframe = desktopIframeRef.current;
      const mobileIframe = mobileIframeRef.current;
      
      let sourceDevice: 'desktop' | 'mobile' | null = null;
      if (event.source === desktopIframe?.contentWindow) {
        sourceDevice = 'desktop';
      } else if (event.source === mobileIframe?.contentWindow) {
        sourceDevice = 'mobile';
      } else {
        return; // 不明なソースからのメッセージは無視
      }
      
      if (event.data.type === 'WIDGET_INITIALIZED') {
        // iframeWidget初期化完了
        if (sourceDevice === 'desktop') {
          desktopIframeLoadedRef.current = true;
        } else {
          mobileIframeLoadedRef.current = true;
        }
      } else if (event.data.type === 'WIDGET_STATE_CHANGE') {
        // 子(iframe)から状態変更通知を受け取り、該当デバイスのstateを更新
        // 状態変更通知を受信
        if (sourceDevice === 'desktop') {
          setDesktopWidgetOpen(event.data.isOpen);
        } else {
          setMobileWidgetOpen(event.data.isOpen);
        }
      } else if (event.data.type === 'REQUEST_INITIAL_STATE') {
        // 子(iframe)からの初期状態要求に応答
        const widgetState = sourceDevice === 'desktop' ? desktopWidgetOpen : mobileWidgetOpen;
        // 初期状態を送信
        const targetIframe = sourceDevice === 'desktop' ? desktopIframe : mobileIframe;
        if (targetIframe?.contentWindow) {
          targetIframe.contentWindow.postMessage({
            type: 'RESTORE_STATE',
            isOpen: widgetState,
          }, '*');
        }
      } else if (event.data.type === 'SAVE_WIDGET_STATE') {
        // 既存の状態保存処理（通常モード用）
        // ウィジェット状態保存
        try {
          sessionStorage.setItem('preview_widget-state', JSON.stringify(event.data.state));
        } catch (error) {
          // 状態保存エラー
        }
      } else if (event.data.type === 'LOAD_WIDGET_STATE') {
        // 既存の状態読み込み処理（通常モード用）
        // ウィジェット状態読み込み要求
        try {
          const savedState = sessionStorage.getItem('preview_widget-state');
          const targetIframe = sourceDevice === 'desktop' ? desktopIframe : mobileIframe;
          if (targetIframe?.contentWindow) {
            targetIframe.contentWindow.postMessage({
              type: 'WIDGET_STATE_RESPONSE',
              state: savedState ? JSON.parse(savedState) : null
            }, '*');
          }
        } catch (error) {
          // 状態読み込みエラー
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [desktopWidgetOpen, mobileWidgetOpen]);

  // URL プレビュー用のHTMLを生成（外部ファイルを使用してCSP問題を回避）
  const generateUrlPreviewHTML = useCallback((url: string) => {
    const currentOrigin = window.location.origin;
    
    // 設定にアイコンURLを追加（カスタムアイコンのみURLに変換、プリセットは数値のまま保持）
    const configWithUrls = {
      ...settings,
      bubbleIcon: settings.bubbleIcon === 'custom' ? settings.bubbleIconUrl : settings.bubbleIcon,
      userIcon: settings.userIcon === 'custom' ? settings.userIconUrl : settings.userIcon,
      botIcon: settings.botIcon === 'custom' ? settings.botIconUrl : settings.botIcon,
      sendIcon: settings.sendIcon === 'custom' ? settings.sendIconUrl : settings.sendIcon,
      closeIcon: settings.closeIcon === 'custom' ? settings.closeIconUrl : settings.closeIcon,
      iconBaseUrl: currentOrigin,
      widgetBaseUrl: currentOrigin
    };

    // URL プレビューHTML生成開始
    
    const configString = JSON.stringify(configWithUrls);

    return `<!DOCTYPE html>
<html lang="ja">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>URL Preview</title>
  <style>
    html, body { 
      margin: 0; 
      padding: 0; 
      width: 100%;
      height: 100%;
      overflow: hidden;
      background-color: ${currentDevice === 'mobile' ? '#ffffff' : '#f3f4f6'};
    }
    iframe { 
      width: 100%; 
      height: 100%; 
      border: none; 
    }
    .error-message {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      padding: 20px;
      background: #f8f9fa;
      border: 1px solid #dee2e6;
      border-radius: 8px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      color: #495057;
      max-width: 400px;
      z-index: 999999999;
      display: none;
    }
  </style>
</head>
<body>
  <!-- 外部サイト表示用 iframe -->
  <iframe id="siteFrame" src="${url}" sandbox="allow-scripts allow-same-origin allow-popups allow-forms"></iframe>
  
  <!-- エラーメッセージ -->
  <div id="errorMessage" class="error-message">
    <h3 style="margin-bottom: 10px; color: #dc3545;">表示できません</h3>
    <p style="margin-bottom: 15px; line-height: 1.5;">
      このサイトはセキュリティ設定により iframe での表示を許可していません。
    </p>
    <p style="font-size: 14px; color: #6c757d; margin-bottom: 15px;">
      URL: ${url}
    </p>
    <p style="font-size: 12px; color: #6c757d;">
      サンプルサイトをお試しください。
    </p>
  </div>

  <!-- チャットボットウィジェット初期化スクリプト -->
  <script>
    (function() {
      // プレビュー環境であることをウィジェットに伝える
      window.IS_PREVIEW_MODE = true;
      window.FORCE_DEVICE_TYPE = '${currentDevice}';

      const config = ${configString};

      // iframe エラーハンドリング
      const siteFrame = document.getElementById('siteFrame');
      const errorMessage = document.getElementById('errorMessage');
      let hasLoaded = false;
      
      const loadTimeout = setTimeout(() => {
        if (!hasLoaded) {
          // Site iframe load timeout
          siteFrame.style.display = 'none';
          errorMessage.style.display = 'block';
        }
      }, 8000);
      
      siteFrame.onload = () => {
        hasLoaded = true;
        clearTimeout(loadTimeout);
        // Site iframe loaded successfully
      };

      // チャットボットスクリプト読み込み
      const script = document.createElement('script');
      script.src = '${currentOrigin}/chatbot-widget.js?v=' + Date.now();
      
      script.onload = () => {
        // URL Preview: ChatBot script loaded
        
        // 初期化を複数回試行
        let attempts = 0;
        const maxAttempts = 3;
        
        function tryInit() {
          attempts++;
          // URL Preview: Initialization attempt
          
          if (window.ChatbotWidget && typeof window.ChatbotWidget.init === 'function') {
            try {
              // 正しい初期化方法
              const widget = window.ChatbotWidget.init(config);
              // URL Preview: ChatBot widget initialized successfully
              
              // 初期化完了後、即座に状態復元要求（ウィジェット内のsetTimeoutを待たない）
              if (window.parent && window.parent !== window) {
                window.parent.postMessage({
                  type: 'REQUEST_INITIAL_STATE'
                }, '*');
              }
              
              return true;
            } catch (e) {
              // URL Preview: Widget initialization failed
            }
          } else {
            // URL Preview: ChatbotWidget.init not available yet
          }
          
          if (attempts < maxAttempts) {
            setTimeout(tryInit, 200);
          } else {
            // URL Preview: Failed to initialize after max attempts
          }
          return false;
        }
        
        // 初期化開始（待ち時間を短縮）
        setTimeout(tryInit, 100);
      };
      
      script.onerror = () => {
        // URL Preview: Failed to load chatbot-widget.js
      };

      document.head.appendChild(script);

      // 親ウィンドウからの設定更新を受信
      window.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'UPDATE_CONFIG' && window.ChatbotWidget) {
          // URL Preview: Config update received
          try {
            window.ChatbotWidget.updateConfig(event.data.config);
          } catch (e) {
            // URL Preview: Config update failed
          }
        }
      });
    })();
  </script>
</body>
</html>`;
  }, [settings, currentDevice]);

  // iframeの初期化
  useEffect(() => {
    // PreviewPanel iframe 初期化開始
    
    // HTML content を生成
    const htmlContent = (showUrlPreview && previewUrl) ? 
      generateUrlPreviewHTML(previewUrl) : 
      generateIframeHTML();
    
    // デバイスが変更されたら、対応するiframeを初期化する
    if (currentDevice === 'desktop') {
      initializeIframe(desktopIframeRef, htmlContent);
    } else {
      initializeIframe(mobileIframeRef, htmlContent);
    }
  }, [settings, currentDevice, showUrlPreview, previewUrl, initializeIframe, generateIframeHTML]);

  // 設定変更時にiframeに送信
  useEffect(() => {
    if (getCurrentIframeLoadedRef().current) {
      // 設定変更 - iframeに送信
      sendConfigToIframe(settings);
    }
  }, [settings, sendConfigToIframe]);

  // デバイス変更を監視
  useEffect(() => {
    // PreviewPanel: デバイス変更検知
  }, [currentDevice]);


  // PreviewPanel: currentDevice tracked
  
  return (
    <div className="flex-1 bg-gray-100 relative flex flex-col">
      {/* 共通ヘッダー（URLプレビュー設定含む） */}
      <div className="p-3 space-y-2 z-10">
        <div className="inline-block bg-black rounded-md px-2 py-1">
          <h2 className="text-xs font-medium text-white">
            {currentDevice === 'desktop' ? 'デスクトップ' : 'モバイル'}プレビュー
          </h2>
        </div>
        
        {/* URLプレビュー設定（共通コンポーネント） */}
        <UrlPreviewSettings
          showUrlPreview={showUrlPreview}
          setShowUrlPreview={setShowUrlPreview}
          previewUrl={previewUrl}
          setPreviewUrl={setPreviewUrl}
          idPrefix={currentDevice}
        />
      </div>

      {/* プレビューコンテナ */}
      <div className="flex-1 flex items-center justify-center overflow-hidden">
        {/* デスクトップ表示 */}
        <div 
          className="relative w-full h-full"
          style={{ display: currentDevice === 'desktop' ? 'block' : 'none' }}
        >
          <iframe
            ref={desktopIframeRef}
            className="w-full h-full border-0 bg-gray-100"
            style={{
              minWidth: '300px',
              minHeight: '200px'
            }}
            title="チャットボットプレビュー"
            sandbox="allow-scripts allow-modals allow-same-origin allow-popups allow-forms"
          />
        </div>

        {/* モバイル表示 */}
        <div
          className="relative"
          style={{ display: currentDevice === 'mobile' ? 'block' : 'none' }}
        >
          {/* iPhone風フレーム */}
          <div className="relative bg-black rounded-[3rem] p-2" style={{ width: '320px', height: '640px' }}>
            {/* ノッチ */}
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 bg-black rounded-b-2xl" 
                 style={{ width: '120px', height: '20px', zIndex: 1000 }}></div>
            
            {/* スクリーン */}
            <div className="relative bg-white rounded-[2.5rem] w-full h-full overflow-hidden">
              {/* ステータスバー */}
              <div className="absolute top-0 left-0 right-0 h-12 bg-white flex items-center justify-between px-4 text-black text-sm z-50">
                <span className="font-semibold">16:02</span>
                <div className="flex items-center space-x-1">
                  <span className="text-xs">●●●●○</span>
                  <span className="text-xs">5G</span>
                  <div className="w-6 h-3 border border-black rounded-sm">
                    <div className="w-4 h-1 bg-black rounded-xs mt-0.5 ml-0.5"></div>
                  </div>
                </div>
              </div>
              
              {/* iframe */}
              <iframe
                ref={mobileIframeRef}
                className="w-full h-full border-0"
                style={{ marginTop: '48px', height: 'calc(100% - 48px)' }}
                sandbox="allow-scripts allow-modals allow-same-origin allow-popups allow-forms"
                title="Mobile Chatbot Preview"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
